import { cloneElement } from 'react';
import classNames from 'classnames';
import { Heading, HeadingProps } from '@components/text';
import { LazyLoadWrapper, LazyLoadWrapperProps } from '@components/utils';
import './HeadingLogoContainer.scss';

type HeadingLogoVariantType = 'default' | 'media-manager' | 'hero';

export interface HeadingLogoContainerProps {
  /** The URL of the logo image. */
  imgUrl?: string;
  /** The size variation. Default large. */
  size?: 'small' | 'medium' | 'large';
  /** Size variant - uses different logo heights and text sizes if 'media-manager' */
  variant?: HeadingLogoVariantType;
  /** The header title. Used as the image `alt` unless `headingLevel` is set,
   * in which case the image is decorative and the title is exposed via the heading. */
  title: string;
  /** Overrides the variant's default heading level. With `imgUrl`, also renders a
   * visually-hidden heading so the title is exposed; the image becomes decorative */
  headingLevel?: NonNullable<HeadingProps['ariaLevel']>;
  /** optional override props for the heading */
  headingProps?: Omit<HeadingProps, 'children' | 'ariaLevel'>;
  /** should image lazy load */
  lazy?: boolean;
  /** props for react-lazy-load-image-component */
  lazyProps?: LazyLoadWrapperProps['lazyProps'];
  /** optional additional class */
  className?: string;
  /** callback fired when image loading state changes */
  onImageStateChange?: LazyLoadWrapperProps['onImageStateChange'];
}

/**
 * HeadingLogoContainer - component that displays a logo or a fallback title
 * @param imgUrl - The URL of the logo image.
 * @param size - The size variation. default large
 * @param variant - Size variant - uses different logo heights and text sizes if 'media-manager'
 * @param title - The header title. Used as the image `alt` when `imgUrl` is provided, unless `headingLevel` is set — in which case the image is decorative (`alt=""`) and the title is exposed via a visually-hidden heading.
 * @param headingLevel - Overrides the variant's default heading level. With `imgUrl`, also renders a visually-hidden `<h{N}>` so the title is exposed to AT while the logo image stays decorative.
 * @param headingProps - optional override props for the heading
 * @param lazy - Should image lazy load
 * @param lazyProps - props for react-lazy-load-image-component
 * @param className - optional additional class
 * @param onImageStateChange - callback fired when image loading state changes
 */
export const HeadingLogoContainer: React.FC<HeadingLogoContainerProps> = ({
  imgUrl,
  size = 'large',
  variant = 'default',
  title,
  headingLevel,
  headingProps,
  lazy,
  lazyProps,
  className,
  onImageStateChange,
  ...rest
}) => {
  interface HeadingVariantProps extends Omit<HeadingProps, 'children'> {
    className?: string;
    lineClamp?: number;
  }

  type HeadingVariantsMap = {
    [key in HeadingLogoVariantType]: HeadingVariantProps;
  };

  const headingVariants: HeadingVariantsMap = {
    default: {
      ariaLevel: 1,
      variant: 'subtitle-01',
      weight: 'bold',
      lineClamp: 2,
      size: size,
      className: 'vds-heading-logo-container__heading',
    },
    'media-manager': {
      ariaLevel: 2,
      variant: 'subtitle-02',
      weight: 'semibold',
      size: size === 'small' ? 'medium' : 'large',
      className: 'vds-heading-logo-container__heading',
    },
    hero: {
      ariaLevel: 2,
      variant: 'heading-01',
      weight: 'bold',
      lineClamp: 2,
      size: size === 'small' ? 'medium' : 'large',
      className: 'vds-heading-logo-container__heading',
    },
  };

  const heading = (
    <Heading
      {...headingVariants[variant]}
      {...headingProps}
      {...(headingLevel ? { ariaLevel: headingLevel } : {})}>
      {title}
    </Heading>
  );

  const VisuallyHiddenHeadingTag = `h${headingLevel ?? 1}` as const;
  const visuallyHiddenHeading = headingLevel ? (
    <VisuallyHiddenHeadingTag className="vds-heading-logo-container__visually-hidden-heading">
      {title}
    </VisuallyHiddenHeadingTag>
  ) : null;

  return (
    <div
      className={classNames(
        'vds-heading-logo-container',
        `vds-heading-logo-container--${size}`,
        `vds-heading-logo-container--${variant}`,
        className,
      )}
      {...rest}>
      {imgUrl ? (
        <>
          {visuallyHiddenHeading}
          <LazyLoadWrapper
            className={classNames(
              'vds-heading-logo-container__img',
              `vds-heading-logo-container__img--${size}`,
            )}
            src={imgUrl}
            alt={headingLevel ? '' : title}
            fallback={
              headingLevel
                ? // The visually-hidden heading already exposes the title to AT;
                  // hide the visible fallback from AT to avoid double announcement.
                  // Clone so we can add `aria-hidden` without changing the underlying heading element.
                  cloneElement(heading, { 'aria-hidden': true })
                : heading
            }
            lazy={lazy}
            lazyProps={lazyProps}
            onImageStateChange={onImageStateChange}
          />
        </>
      ) : (
        heading
      )}
    </div>
  );
};
